import { gsap } from "gsap";
import { CustomBounce } from "gsap/CustomBounce";
import { CustomEase } from "gsap/CustomEase";
import { CustomWiggle } from "gsap/CustomWiggle";
import { ExpoScaleEase, RoughEase, SlowMo } from "gsap/EasePack";

import type { ElementAnimation } from "../types";

import type { AnimationCategory, AnimationPreset, ParamSchema } from "./types";

// EasePack eases register individually (the EasePack object has no plugin hook)
gsap.registerPlugin(
  CustomEase,
  CustomWiggle,
  CustomBounce,
  RoughEase,
  SlowMo,
  ExpoScaleEase,
);

// named plugin eases referenced by EASING_OPTIONS (types.ts) — created once
// at module load so gsap.parseEase resolves them anywhere
CustomWiggle.create("wiggle6", { wiggles: 6, type: "easeOut" });
CustomWiggle.create("wiggle12", { wiggles: 12, type: "uniform" });
CustomBounce.create("bounceSoft", { strength: 0.4 });

const baseDefaults = {
  duration: 1000,
  delay: 0,
  repeat: 0,
  easing: "power1.inOut",
};

// direction: 0=left, 1=right, 2=up, 3=down, then diagonals 4=up-left,
// 5=up-right, 6=down-left, 7=down-right (normalized so Distance px stays true)
const DIAG = Math.SQRT1_2;
export const SLIDE_VECTORS: [number, number][] = [
  [-1, 0],
  [1, 0],
  [0, -1],
  [0, 1],
  [-DIAG, -DIAG],
  [DIAG, -DIAG],
  [-DIAG, DIAG],
  [DIAG, DIAG],
];

/** Maps a UI direction (0=left, 1=right, 2=up, 3=down — the way the wipe
 * edge travels) to the renderer's revealDir (which side the reveal grows
 * from: 0=left, 1=right, 2=top, 3=bottom). */
export const WIPE_TO_REVEAL_DIR = [1, 0, 3, 2];

export const rad = (degrees: number) => (degrees * Math.PI) / 180;

/** Runtime-internal marker set on the effective config of every clip after an
 * element's first (never persisted — see AnimationRuntime.compile). */
export const CHAINED_CLIP = Symbol("chainedClip");

export const isChainedClip = (cfg: ElementAnimation) =>
  (cfg as Record<PropertyKey, unknown>)[CHAINED_CLIP] === true;

/** GSAP can't parse CSS `cubic-bezier(...)` strings (what the easing editor
 * saves) — an unknown ease silently falls back to the default. Convert them
 * through CustomEase (which takes the bare numbers), cached per curve. */
const bezierEases: Record<string, gsap.EaseFunction> = {};
export const toGsapEase = (easing: ElementAnimation["easing"]) => {
  if (typeof easing === "string") {
    const match = easing.match(/^cubic-bezier\(([^)]+)\)$/);
    if (match) {
      return (bezierEases[easing] ??= CustomEase.create(
        `clip-bezier-${Object.keys(bezierEases).length}`,
        match[1],
      ));
    }
  }
  return easing;
};

export const timing = (cfg: ElementAnimation) => ({
  duration: cfg.duration / 1000,
  delay: cfg.delay / 1000,
  repeat: cfg.repeat,
  yoyo: cfg.yoyo === true,
  repeatDelay: Math.max(0, cfg.repeatDelayMs ?? 0) / 1000,
  ease: toGsapEase(cfg.easing),
  // a fromTo's from-values render at the whole timeline's first frame by
  // default (immediateRender), which poisons earlier clips tweening the same
  // channel on the shared target. The runtime marks every clip after an
  // element's first; those must wait until the playhead reaches them.
  ...(isChainedClip(cfg) ? { immediateRender: false } : {}),
});

/** Applies static (non-tweened) channel values at the clip's start via a
 * zero-duration tween, instead of assigning them on the target at build time —
 * a build-time write is "last clip wins" across ALL of an element's clips,
 * so e.g. wipeIn(left) + wipeOut(down) would both reveal from the bottom. */
export const setStatic = (
  tl: gsap.core.Timeline,
  target: object,
  values: Record<string, unknown>,
) => tl.set(target, { ...values, immediateRender: false }, 0);

/** Object-syntax keyframes spread across the configured duration. */
export const kf = (
  cfg: ElementAnimation,
  frames: Record<string, number[]>,
  easeEach = "none",
) => ({
  keyframes: { ...frames, easeEach },
  ...timing(cfg),
});

/** Samples a parametric path t∈[0,1] → [dx, dy] into keyframe arrays. */
export const samplePath = (
  fn: (t: number) => [number, number],
  samples = 36,
) => {
  const dx: number[] = [];
  const dy: number[] = [];
  for (let i = 0; i <= samples; i++) {
    const [x, y] = fn(i / samples);
    dx.push(x);
    dy.push(y);
  }
  return { dx, dy };
};

export const definePreset = (
  id: string,
  name: string,
  category: AnimationCategory,
  opts: {
    params?: Record<string, number>;
    schema?: ParamSchema[];
    easing?: string;
    duration?: number;
    repeat?: number;
    /** physics-based default: overrides easing+duration at compile time
     * (the spring's settling time defines the duration) */
    spring?: ElementAnimation["spring"];
    /** default keyframe tracks (the `keyframed` preset) */
    tracks?: ElementAnimation["tracks"];
  },
  build: AnimationPreset["build"],
): AnimationPreset => ({
  id,
  name,
  category,
  defaults: {
    duration: opts.duration ?? baseDefaults.duration,
    delay: 0,
    repeat: opts.repeat ?? 0,
    easing: opts.easing ?? baseDefaults.easing,
    ...(opts.spring ? { spring: opts.spring } : {}),
    ...(opts.tracks ? { tracks: opts.tracks } : {}),
    params: opts.params ?? {},
  },
  paramsSchema: opts.schema ?? [],
  build,
});

export const DIRECTION_SCHEMA: ParamSchema = {
  key: "direction",
  label: "Direction",
  min: 0,
  max: 7,
  step: 1,
};

/** 4-way variant for edge-based reveals (wipe/draw): the renderer's
 * revealDir only grows from a side, so diagonals are not offered. */
export const EDGE_DIRECTION_SCHEMA: ParamSchema = {
  key: "direction",
  label: "Direction",
  min: 0,
  max: 3,
  step: 1,
};

export const DISTANCE_SCHEMA: ParamSchema = {
  key: "distance",
  label: "Distance px",
  min: 10,
  max: 1000,
  step: 10,
};

export const DEGREES_SCHEMA: ParamSchema = {
  key: "degrees",
  label: "Degrees",
  min: -1080,
  max: 1080,
  step: 15,
};
